import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { WarningCircle, ArrowSquareOut, X } from "@phosphor-icons/react";

import { getDrillContent } from "../../lib/indigo/drill-content";
import { useFeatureFlag } from "../../lib/featureFlags";

/**
 * P2.1 W8 — Per-metric detail drawer.
 *
 * Slides up from the bottom (mobile) / right (desktop). Reads
 * drill-content.ts for the metric's title / formula / interpretation /
 * concern thresholds / methodology link. Falls back to a generic
 * "no drawer yet for this metric" hint when the metric isn't in the
 * registry — so adding a new metric doesn't crash the UI.
 *
 * Usage:
 *
 *   <MetricDetailDrawer
 *     metricName="ctl"
 *     isOpen={openMetric === "ctl"}
 *     onClose={() => setOpenMetric(null)}
 *   />
 *
 * Inspector / MetricChip integration is the obvious wire-up, but the
 * drawer is decoupled — any view that holds a metric name can render it
 * (e.g. a future "explain this number" tooltip on Today widgets).
 */

export interface MetricDetailDrawerProps {
  metricName: string | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Заголовок для метрики, у которой ещё нет карточки в drill-content.
 *
 * Было `metricName.toUpperCase()` — и ключ уходил на экран как есть:
 * «HRV_Z_SCORE», «CV_HRV», «VO2MAX_TREND». Тот же класс, что «ACWR_HIGH»
 * и «half» из v197: строка-идентификатор вместо текста. Карточек 45, ключей
 * больше, так что фолбэк виден не в теории.
 *
 * Подчёркивания на пробелы, регистр верхний — аббревиатуры (HRV, CTL, VO2MAX)
 * так и остаются собой, а составной ключ читается как «HRV Z SCORE».
 * Полноценные названия появятся вместе с карточками, это лишь не даёт
 * показать пользователю кусок кода.
 */
export function humanizeMetricKey(key: string): string {
  return key.replace(/[_-]+/g, " ").trim().toUpperCase();
}

export function MetricDetailDrawer({
  metricName,
  isOpen,
  onClose,
}: MetricDetailDrawerProps) {
  const { t } = useTranslation("common");
  // ═══ ДВА ЯЗЫКА ИЗ ЧЕТЫРЁХ — 2026-08-07 ═══
  //
  // Здесь стояло `i18n.language.startsWith("ru") ? "ru" : "en"`, а текст
  // лежал в TS-реестре на двух языках. Немец и украинец получали
  // английскую карточку в приложении, где переведено всё остальное.
  // Теперь текст берётся из словаря `drill` обычным путём, и язык
  // выбирает i18n — как везде.
  const { t: td } = useTranslation("drill");
  // Phase 7 v149 — verbose mode опционально показывает full methodology URL
  // (handy for screenshots, citation, audit), gated by featureFlags.
  const verbose = useFeatureFlag("verbose_drill_drawer");

  useEffect(() => {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  if (!isOpen || !metricName) return null;

  const content = getDrillContent(metricName);
  // `when_concerning` — массив, и i18n отдаёт его только по явной просьбе.
  // Без `returnObjects` сюда пришла бы строка, а `.map` по строке молча
  // разложил бы её на буквы.
  const raw = content
    ? td(`${metricName}.when_concerning`, { returnObjects: true })
    : null;
  const concerns: string[] = Array.isArray(raw) ? (raw as string[]) : [];

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="metric-detail-drawer"
      data-metric={metricName}
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-bg-base/70 backdrop-blur-sm"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full md:max-w-2xl rounded-t-card md:rounded-card bg-bg-card shadow-drawer max-h-[85vh] overflow-y-auto">
        <header className="sticky top-0 bg-bg-card border-b border-bg-border px-5 py-3 flex items-baseline justify-between">
          <div>
            <div className="text-[10px] font-mono font-bold uppercase tracking-[0.22em] text-fg-muted">
              {td("ui.eyebrow", { defaultValue: "МЕТРИКА · РАЗБОР" })}
            </div>
            <h2
              className="text-lg font-bold text-fg-primary mt-0.5"
              data-testid="metric-drawer-title"
            >
              {content
                ? td(`${metricName}.title`)
                : humanizeMetricKey(metricName)}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("a11y.close", { defaultValue: "Закрыть" })}
            className="text-fg-muted hover:text-fg-primary"
          >
            <X size={20} aria-hidden />
          </button>
        </header>

        {!content ? (
          <div
            className="p-6 text-sm text-fg-secondary"
            data-testid="metric-drawer-missing"
          >
            {td("ui.missing", { defaultValue: "Для этой метрики ещё не написана карточка." })}
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Канон: запись расчёта пользователю не показывается — только
                смысл словами. Формула живёт в реестре как справка. */}
            <section>
              <div className="text-[10px] font-mono font-bold uppercase tracking-[0.18em] text-fg-muted mb-1.5">
                {td("ui.interpretation", { defaultValue: "ЧТО ЭТО ЗНАЧИТ" })}
              </div>
              <p
                className="text-sm text-fg-primary leading-relaxed"
                data-testid="metric-drawer-interpretation"
              >
                {td(`${metricName}.interpretation`)}
              </p>
            </section>

            <section>
              <div className="text-[10px] font-mono font-bold uppercase tracking-[0.18em] text-warn mb-1.5">
                {td("ui.when_matters", { defaultValue: "КОГДА ВАЖНО" })}
              </div>
              <ul
                className="space-y-1.5"
                data-testid="metric-drawer-concerns"
              >
                {concerns.map((line, i) => (
                  <li
                    key={i}
                    className="flex gap-2 text-sm text-fg-primary leading-relaxed"
                  >
                    <WarningCircle
                      size={14}
                      className="shrink-0 mt-0.5 text-warn"
                      aria-hidden
                    />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </section>

            <footer className="pt-3 border-t border-bg-border text-xs text-fg-muted">
              {td("ui.methodology", { defaultValue: "Методика: " })}
              <a
                href={content.methodology_link}
                target="_blank"
                rel="noreferrer"
                className="text-accent hover:underline inline-flex items-center gap-1"
                data-testid="metric-drawer-methodology"
              >
                {td("ui.source", { defaultValue: "первоисточник" })}
                <ArrowSquareOut size={11} aria-hidden />
              </a>
              {verbose && (
                <div
                  data-testid="metric-drawer-methodology-url"
                  className="mt-1 break-all text-[10px] text-fg-muted font-mono"
                >
                  {content.methodology_link}
                </div>
              )}
            </footer>
          </div>
        )}
      </div>
    </div>
  );
}
